import { GetGeneralInformationResponse } from "@1games-ru/region-shared/dist/backend/requests/faction";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { dateFormat } from "@/core/helpers/dateFormat";
import dateTimeFromUnix from "@/core/helpers/dateTimeFromUnix";
import { daysDiffFromNow } from "@/core/helpers/daysDiffFromNow";
import { unixFromDateTime } from "@/core/helpers/unixFromDateTime";
import FactionExitButton from "@/domains/faction/components/FactionExitButton";
import FactionProgressInfo from "@/domains/faction/components/FactionProgressInfo";
import FactionTextBlock from "@/domains/faction/components/FactionTextBlock";

type Props = {
  src: string;
  data: GetGeneralInformationResponse;
  onDismiss: () => void;
  color?: string;
};

export default function FactionInfoAside({ src, data, onDismiss, color }: Props) {
  const { t } = useTranslation("faction");

  const personalInfo = data?.selfMember;
  const rankPosition = useMemo(
    () => data.rankPositions.find((item) => item.id === personalInfo.rankPositionId),
    [data.rankPositions, personalInfo.rankPositionId],
  );
  const rank = useMemo(
    () => data.ranks.find((item) => item.id === personalInfo.rankId),
    [data.ranks, personalInfo.rankId],
  );

  const daysInFaction = useMemo(() => {
    if (!personalInfo) return 0;
    const dateUnix = unixFromDateTime(personalInfo.createdAt);
    return daysDiffFromNow(dateUnix);
  }, [personalInfo]);

  return (
    <div className="relative z-2 flex flex-col w-[19.0625rem] h-full">
      <div className="relative flex flex-col h-[25rem] bg-dark-800 rounded-xl">
        <div
          className="absolute z-1 top-0 left-0 size-full"
          style={{
            background: `radial-gradient(21.81% 23.08% at 100% 69.95%, ${color}3D 0%, ${color}1F 42.17%, rgba(24, 24, 24, 0.00) 100%)`,
          }}
        />
        <div
          className="relative flex items-end w-full h-[20.5rem] px-6 pb-1 bg-cover bg-no-repeat bg-center rounded-xl"
          style={{ backgroundImage: `url(${src})` }}
        >
          <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-dark-800 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-dark-800 to-transparent" />
          <FactionTextBlock
            className="absolute top-5 left-5 h-auto py-[0.4375rem] px-2.5 bg-dark-900 rounded-md"
            classNameText1="text-[#B8B8B8] normal-case"
            text={`sID ${personalInfo.characterId}`}
          />
          <div className="relative z-1 w-full">
            <div className="flex flex-col mb-1">
              <div className="mb-1 text-18 font-bold">
                {personalInfo?.firstName} {personalInfo?.lastName}
              </div>
              <div className="text-14 font-medium text-[#B8B8B8]">{rankPosition?.name}</div>
            </div>
            <div className="relative flex justify-between items-end w-full">
              <FactionTextBlock text={rank?.name} className="w-[11.875rem]" />
              <FactionTextBlock
                text={rank?.level}
                text2={t("faction.rank")}
                className="flex-col h-auto p-0 bg-transparent"
                classNameText1="text-[1.75rem]"
                classNameText2="text-white"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center mx-6.5 mb-4 pt-4">
          <div className="text-14 font-medium text-[#B8B8B8] leading-none">
            {t("faction.daysInFraction")}:
          </div>
          <div className="text-14 font-medium leading-none">{daysInFaction}</div>
        </div>
        <div className="flex justify-between items-center mx-6.5 pb-5.5">
          <div className="text-14 font-medium text-[#B8B8B8] leading-none">
            {t("faction.dateOfEntry")}:
          </div>
          <div className="text-14 font-medium leading-none">
            {dateFormat(personalInfo?.createdAt)}
          </div>
        </div>
      </div>
      {/* TODO внести корректные данные, когда они появятся ||||| */}
      <FactionProgressInfo
        currentCount={100}
        maxCount={200}
        className="my-3.5"
        color={color}
        isSoon
      />
      <FactionExitButton onClick={onDismiss} />
    </div>
  );
}
